<template>
  <modal-template
    ref="modal"
    :title="$t('puzzle.importModal.title')"
    type-modal="import_file_modal"
    @close="onCancel"
  >
    <div class="modal-content__main import-modal-container">
      <d-row>
        <d-col cols="12" class="mb-3">
          <d-card class="mb-3 shadow-none border rounded">
            <d-card-header
              class="p-2 d-flex justify-content-between w-100 rounded cursor-pointer"
              role="tab"
              @click.native="showInstructions = !showInstructions"
              style="background-color: #f8f9fa;"
            >
              <div class="d-flex align-items-center small font-weight-bold text-muted">
                <i
                  class="fas fa-chevron-right mr-2 transition-icon"
                  :class="{ 'rotated': showInstructions }"
                />
                {{ $t('puzzle.importModal.howToFormat') }}
              </div>
            </d-card-header>

            <d-collapse :visible="showInstructions" id="#">
              <div class="px-3 pb-3">
                <p class="mb-2 small">{{ $t('puzzle.importModal.followModel') }}</p>
                <ul class="pl-3 mb-2 extra-small">
                  <li>
                    {{ $t('puzzle.importModal.letters') }}
                  </li>
                  <li>
                    {{ $t('puzzle.importModal.dots') }}
                  </li>
                  <li>
                    {{ $t('puzzle.importModal.size') }}
                  </li>
                </ul>
                <textarea
                  readonly
                  class="form-control example-textarea"
                  rows="10"
                  :value="exampleContent"
                />

                <div class="text-right">
                  <a href="javascript:void(0)" @click="downloadExample" class="download-link">
                    <i class="fas fa-download mr-1"></i>
                    {{ $t('puzzle.importModal.downloadExample') }}
                  </a>
                </div>
              </div>
            </d-collapse>
          </d-card>
          <div :class="['import-container']">
            <drop-down :callback="onFileSelect" :has-error="error"/>

            <div v-if="error" class="invalid-feedback d-block text-right">
              {{ errorMessage }}
            </div>

            <puzzle-preview
              v-if="previewData.rows > 0 && !error"
              :table="previewData.table"
              :rows="previewData.rows"
              :columns="previewData.columns"
            />
          </div>
        </d-col>
      </d-row>
    </div>

    <div slot="actions" class="modal-content__control">
      <d-button
        type="button"
        @click="onCancel"
        class="btn btn-sm btn-secondary mr-2"
      >
        {{ $t('shared.actions.cancel') }}
      </d-button>
      <d-button
        type="button"
        @click="onConfirm"
        class="btn btn-sm btn-primary"
        :disabled="!fileRawContent || error"
      >
        {{ $t('puzzle.importModal.confirm') }}
      </d-button>
    </div>
  </modal-template>
</template>

<script>
import { mapActions } from 'vuex';
import ModalTemplate from '_shared/Modal.vue';
import DropDown from '_shared/DropDown.vue';
import { EXAMPLE_GRID_FILE } from '../PuzzleModel';
import PuzzlePreview from '../PuzzlePreview.vue';

export default {
  name: 'import-file-modal',
  components: {
    ModalTemplate,
    DropDown,
    PuzzlePreview,
  },
  data() {
    return {
      fileRawContent: null,
      error: false,
      errorMessage: '',
      showInstructions: false,
      previewData: {
        rows: 0,
        columns: 0,
        table: [],
      },
    };
  },
  computed: {
    exampleContent() {
      return EXAMPLE_GRID_FILE.join('\n');
    },
  },
  methods: {
    ...mapActions(['setToggle', 'activeAlert']),
    onFileSelect(file) {
      this.reset();
      if (!file) return;

      if (!file.name.toLowerCase().endsWith('.txt')) {
        this.setError(this.$t('puzzle.importModal.invalidFormat'));
        return;
      }

      const reader = new FileReader();
      reader.onload = e => {
        this.showInstructions = false;
        this.processPreview(e.target.result);
      };
      reader.readAsText(file);
    },
    processPreview(content) {
      const lines = content.trim().split('\n').map(l => l.replace(/(\r)/gm, '').trim());
      const rows = lines.length;
      const cols = lines[0].length;
      const validPattern = /^[a-zA-Z.]+$/;
      const hasInvalidChars = lines.some(line => !validPattern.test(line));

      if (hasInvalidChars) {
        this.setError(this.$t('puzzle.importModal.invalidChars'));
        return;
      }

      if (lines.some(l => l.length !== cols)) {
        this.setError(this.$t('puzzle.importModal.invalidRows'));
        return;
      }

      const tempTable = [];
      lines.forEach((row, r) => {
        Array.from(row).forEach((char, c) => {
          tempTable.push({
            text: char === '.' ? '' : char.toUpperCase(),
            isAnswer: char !== '.',
            positionx: c,
            positiony: r,
            children: [],
            settings: {},
          });
        });
      });

      this.fileRawContent = content;
      this.previewData = { rows, columns: cols, table: tempTable };
    },
    setError(msg) {
      this.error = true;
      this.showInstructions = false;
      this.errorMessage = msg;
    },
    reset() {
      this.error = false;
      this.fileRawContent = null;
      this.previewData = { rows: 0, columns: 0, table: [] };
    },
    downloadExample() {
      const blob = new Blob([this.exampleContent], { type: 'text/plain' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'exemplo_jogo_cruzadas.txt';
      link.click();
      window.URL.revokeObjectURL(url);
    },
    onCancel() {
      this.reset();
      this.setToggle({ type: 'import_file_modal', bool: false });
    },
    onConfirm() {
      if (this.fileRawContent && !this.error) {
        this.$emit('confirm-import', this.fileRawContent);
        this.onCancel();
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.error-message {
  color: #dc3545;
  font-size: 0.85rem;
  font-weight: 500;
}

::v-deep {
  input {
    font-size: 24px !important;
  }
}

.import-modal-container {
  ::v-deep .drop {
    position: relative;
    overflow: hidden;

    input#uploadmytextfile {
      height: 100% !important;
      top: 0 !important;
      position: absolute !important;
      z-index: 1 !important;
    }
  }

  .cursor-pointer {
    cursor: pointer;
  }

  .transition-icon {
    transition: transform 0.3s ease;
    font-size: 12px;
  }

  .rotated {
    transform: rotate(90deg);
  }

  .example-textarea {
    font-family: 'Courier New', Courier, monospace !important;
    font-size: 12px !important;
    background-color: #fcfcfc !important;
    border: 1px double #ccc !important;
    color: #666;
    resize: none;
    letter-spacing: 3px;
    line-height: 1.5;
    padding: 10px;
  }

  .download-link {
    font-size: 11px;
    color: #6c757d;
    text-decoration: none;
    transition: color 0.2s;

    &:hover {
      color: #007bff;
      text-decoration: underline;
    }
  }

  .extra-small {
    font-size: 10.5px;
    letter-spacing: 0.3px;
  }
}
</style>
